import type { ComponentType } from 'react';
import type { TenantComponentLoader } from '../../types';

/**
 * Core screens Keilog replaces — paths RA2N already has, where the route stays
 * core's and only the screen behind it changes (`routes.tsx` is for paths RA2N
 * does not have).
 *
 * The key is the `module/Component` the router's `ModuleLoader` asks for. The
 * loader is dynamic because a static import closes the loop between
 * `customize/registry` and the shared UI a screen imports, rendering it as
 * `undefined`. The cast mirrors `src/runtime/moduleInit.ts`.
 */
export const keilogComponentOverrides: Record<string, TenantComponentLoader> = {
  // ログイン — in place of RA2N's `LoginPage` at `/{site}/login`. Pre-auth, so
  // core's login route hands `ModuleLoader` the site type from the URL.
  'auth/Login': () =>
    import('../pages/login/pages/RahLoginPage').then(module => ({
      default: module.default as ComponentType<unknown>,
    })),

  // 施術者管理 — ConceptDesigns/FCオーナー/therapist-master.html, in place of
  // RA2N's `MassagerPage` at `/{tenant}/massagers/init`.
  'massager/MassagerList': () =>
    import('../pages/therapist-master/pages/TherapistMasterPage').then(module => ({
      default: module.default as ComponentType<unknown>,
    })),

  // スケジュール管理 › 利用者別 (SCR-061-001) — ConceptDesigns/スタッフ/schedule.html#visit,
  // in place of RA2N's `HomeVisitPatientPage` (訪問予定表(患者毎)) at
  // `/{tenant}/visits/patient_init`. It replaces SCR-064 利用者予定, retired with its screen
  // (Assume #1): the path and the menu entry stay, the screen behind them changes. `medix` still
  // meets the core screen.
  'visit/HomeVisitPatientList': () =>
    import('../pages/patient-visit-schedule/pages/PatientVisitSchedulePage').then(module => ({
      default: module.default as ComponentType<unknown>,
    })),

  // スケジュール管理 › 週間表示 (SCR-061-002) — ConceptDesigns/スタッフ/schedule.html#weekly, in place of
  // RA2N's `VisitSchedulePage` (訪問予定, its week calendar) at `/{tenant}/visits/schedule` and the two
  // longer shapes of that path. `medix` still meets the core screen.
  'visit/VisitSchedule': () =>
    import('../pages/schedule-board/pages/ScheduleWeeklyPage').then(module => ({
      default: module.default as ComponentType<unknown>,
    })),

  // SCR-210 書式マスタ (Document format master) — the tenant's own screen: 文章テンプレート across the
  // 基幹店 of the tenant and レセプト書式, for 本部 (core's screen reads the signed-in 治療院 only).
  'documentTemplate/DocumentTemplate': () =>
    import('../pages/document-format-master/pages/DocumentFormatMasterPage').then(module => ({
      default: module.default as ComponentType<unknown>,
    })),
};

export default keilogComponentOverrides;
